import { useEffect, useRef, useState } from "react";
import { best, tiedAtBest, type SideBook } from "../lib/board";
import { cn } from "../lib/cn";
import { fmtOdds, fmtUsd } from "../lib/format";
import { useOddsFormat } from "../lib/odds-format";
import type { Partner } from "../lib/types";
import { Mark } from "./Mark";

/** True for a moment after `value` changes — drives the brand-glow flash on a moved price. */
function useFlash(value: number | null): boolean {
  const prev = useRef(value);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (prev.current === value) return;
    prev.current = value;
    setOn(true);
    const t = setTimeout(() => setOn(false), 900);
    return () => clearTimeout(t);
  }, [value]);
  return on;
}

/**
 * One side of one book: the best price, the size behind it, and who's offering it (with "+N"
 * when other venues match). Clicking opens every venue's quote for that book.
 *
 * Phone width: line over price, no venue. Wider: one row, venue logo on the right.
 */
export function PriceCell({
  side,
  lineText,
  selected,
  onSelect,
  partners,
  missing = "blank",
}: {
  side: SideBook | null;
  /** Pre-formatted line ("+1.5", "o8.5"), or null for a market without lines. */
  lineText: string | null;
  selected: boolean;
  onSelect: () => void;
  partners?: Map<string, Partner>;
  /** A side this book doesn't have: an empty slot (board draw rows) or a dash (full-game view). */
  missing?: "blank" | "dash";
}) {
  const format = useOddsFormat();
  const top = side ? best(side) : null;
  const flash = useFlash(top?.price ?? null);

  if (!side && missing === "blank") return <div aria-hidden className="h-11 sm:h-10" />;
  if (!side || !top) {
    return (
      <div className="flex h-11 items-center justify-center rounded-xl bg-cell/40 text-faint sm:h-10 sm:justify-start sm:px-3">
        —
      </div>
    );
  }

  const ties = tiedAtBest(side);
  const venue = partners?.get(top.partnerId);
  const venueName = venue?.name ?? top.partnerName;
  const count = side.quotes.length;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      title={`Best: ${venueName}${ties ? ` (+${ties} more at this price)` : ""} · ${count} venue${count === 1 ? "" : "s"} quoting`}
      className={cn(
        "flex h-11 w-full min-w-0 flex-col items-center justify-center rounded-xl border leading-tight transition-all duration-150",
        "sm:h-10 sm:flex-row sm:justify-start sm:gap-2 sm:px-3",
        selected
          ? "border-accent bg-cell-hi shadow-[0_0_20px_-8px_var(--color-accent)]"
          : "border-transparent bg-cell hover:-translate-y-px hover:bg-cell-hi",
        flash && "flash",
      )}
    >
      {lineText && (
        <span className="text-[11px] font-medium text-dim sm:text-[13px] sm:font-semibold sm:text-ink">{lineText}</span>
      )}
      <span className="text-[13px] font-bold sm:text-[14px]">{fmtOdds(top.price, top.american, format)}</span>
      <span className="hidden text-[11px] text-dim xl:inline">{fmtUsd(top.available)}</span>
      <span className="ml-auto hidden items-center gap-1 sm:flex">
        <Mark name={venueName} src={venue?.logo_url} size={16} />
        {ties > 0 && <span className="text-[10px] font-semibold text-dim">+{ties}</span>}
      </span>
    </button>
  );
}
